interface SessionState {
    warningStartTime: number | null;
    sessionExtendedAt: number | null;
    lastActivity: number;
    isWarningActive: boolean;
    masterTabId: string | null;
    sessionEndTime: number | null;
    logoutTime: number | null;
}

interface SessionConfig {
    sessionTime: number;
    warningTime: number;
}

type SessionEventType =
    | 'warning-started'
    | 'warning-ended'
    | 'session-extended'
    | 'force-logout'
    | 'activity-detected'
    | 'extension-request'
    | 'master-changed';

interface SessionEvent {
    type: SessionEventType;
    tabId: string;
    timestamp: number;
    data?: any;
}

const STORAGE_KEYS = {
    FORCE_LOGOUT: 'forceLogout',
    SESSION_STATE: 'sessionState',
    SESSION_EVENT: 'sessionEvent',
    TAB_HEARTBEAT: 'tabHeartbeat',
    MASTER_ELECTION: 'masterElection'
} as const;

// Only track meaningful activity that corresponds to backend session activity
// Remove mouse movements and other UI-only events that don't trigger backend requests
const ACTIVITY_EVENTS: string[] = []; // Disabled - will track API requests instead

const isSessionDebugMode = (): boolean => {
    const fconst = (window as any).FCONST;
    const debugValue = fconst?.FS_FRONTEND_SESSION_EXPIRY_DEBUG_MODE;

    // Check for boolean true or string 'true'
    return debugValue === true || debugValue === 'true';
};

// SessionManager module loaded
if (isSessionDebugMode()) {
    console.log('🔄 [SessionManager] Module loaded at', new Date().toISOString());
}

class SessionManager {
    private static instance: SessionManager;
    private tabId: string;
    private isMaster: boolean = false;
    private isConfigured: boolean = false;
    private config: SessionConfig = {sessionTime: 0, warningTime: 0};
    private listeners: Map<SessionEventType, Set<(event: SessionEvent) => void>> = new Map();

    private warningTimer: ReturnType<typeof setTimeout> | null = null;
    private logoutTimer: ReturnType<typeof setTimeout> | null = null;
    private countdownInterval: ReturnType<typeof setInterval> | null = null;
    private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
    private masterElectionTimeout: ReturnType<typeof setTimeout> | null = null;

    private activityDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
    private lastActivityBroadcast: number = 0;
    private readonly ACTIVITY_DEBOUNCE_MS = 1000;

    private constructor() {
        this.tabId = this.generateTabId();
        this.setupStorageListener();
        this.setupActivityListeners();
        this.setupApiInterceptors();
        this.startHeartbeat();

        // Set isLoggedIn flag - if SessionManager is initialized, user must be logged in
        // (SessionManager is only loaded in authenticated parts of the app)
        localStorage.setItem('isLoggedIn', 'true');

        // Always log initialization and debug mode status
        const debugMode = isSessionDebugMode();
        const fconst = (window as any).FCONST;

        if (debugMode) {
            console.log('[SessionManager] Initializing...', {
                tabId: this.tabId,
                debugMode,
                hasFCONST: !!fconst,
                debugConfig: fconst?.FS_FRONTEND_SESSION_EXPIRY_DEBUG_MODE,
                debugConfigType: typeof fconst?.FS_FRONTEND_SESSION_EXPIRY_DEBUG_MODE,
                isLoggedIn: true
            });
        }

        if (debugMode) {
            console.log('🔍 [SessionManager] Debug mode enabled - detailed logging active');
            console.log('🔍 [SessionManager] FCONST available:', fconst);
        }

        this.log('SessionManager initialized', {tabId: this.tabId});

        // Delay master election to allow proper initialization
        setTimeout(() => {
            this.electMaster();
        }, 100);
    }

    static getInstance(): SessionManager {
        if (!SessionManager.instance) {
            if (isSessionDebugMode()) {
                console.log('[SessionManager] Creating new instance - called from:', new Error().stack?.split('\n')[2]);
            }
            SessionManager.instance = new SessionManager();
        }
        return SessionManager.instance;
    }

    private generateTabId(): string {
        return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    private log(message: string, data?: any): void {
        if (!isSessionDebugMode()) {return;}

        const logData = {
            timestamp: new Date().toISOString(),
            tabId: this.tabId,
            isMaster: this.isMaster,
            ...data
        };

        if (isSessionDebugMode()) {
            console.log(`[SessionManager] ${message}`, logData);
        }
    }

    private setupStorageListener(): void {
        window.addEventListener('storage', (event) => {
            if (!event.key) {return;}

            switch (event.key) {
                case STORAGE_KEYS.SESSION_EVENT:
                    this.handleSessionEvent(event);
                    break;
                case STORAGE_KEYS.FORCE_LOGOUT:
                    this.handleForceLogout();
                    break;
                case STORAGE_KEYS.MASTER_ELECTION:
                    this.handleMasterElection();
                    break;
                case STORAGE_KEYS.TAB_HEARTBEAT:
                    this.handleTabHeartbeat();
                    break;
            }
        });
    }

    private setupActivityListeners(): void {
        ACTIVITY_EVENTS.forEach(event => {
            window.addEventListener(event, () => this.handleActivity(), {passive: true});
        });
    }

    private setupApiInterceptors(): void {
        // Intercept fetch requests
        const originalFetch = window.fetch;
        window.fetch = (...args) => {
            const [url] = args;
            if (this.isApiRequest(url)) {
                this.trackApiRequest();
            }
            return originalFetch.apply(window, args);
        };

        this.log('API interceptors setup for session tracking');
    }

    private isApiRequest(url: string | Request | URL): boolean {
        let urlString: string;

        if (typeof url === 'string') {
            urlString = url;
        } else if (url instanceof URL) {
            urlString = url.href;
        } else if (url instanceof Request) {
            urlString = url.url;
        } else {
            return false;
        }

        // Only track requests to our API endpoints (not external URLs)
        return urlString.startsWith('/') ||
            urlString.includes(window.location.hostname) ||
            urlString.includes('/internal_api/') ||
            urlString.includes('/api/');
    }

    private handleActivity(): void {
        const now = Date.now();

        if (this.activityDebounceTimeout) {
            clearTimeout(this.activityDebounceTimeout);
        }

        this.activityDebounceTimeout = setTimeout(() => {
            if (now - this.lastActivityBroadcast > this.ACTIVITY_DEBOUNCE_MS) {
                this.broadcastActivity();
                this.lastActivityBroadcast = now;
            }
        }, 100);
    }

    private broadcastActivity(): void {
        this.updateSessionState({lastActivity: Date.now()});
        this.broadcastEvent('activity-detected', {timestamp: Date.now()});

        if (this.isMaster) {
            this.resetTimers();
        }

        this.log('Activity detected and broadcasted');
    }

    private startHeartbeat(): void {
        this.heartbeatInterval = setInterval(() => {
            this.sendHeartbeat();
        }, 5000);
    }

    private sendHeartbeat(): void {
        const heartbeat = {
            tabId: this.tabId,
            timestamp: Date.now(),
            isMaster: this.isMaster
        };
        localStorage.setItem(STORAGE_KEYS.TAB_HEARTBEAT, JSON.stringify(heartbeat));
    }

    private handleTabHeartbeat(): void {
        if (!this.isMaster) {return;}

        setTimeout(() => {
            this.checkForDeadTabs();
        }, 1000);
    }

    private checkForDeadTabs(): void {
        const state = this.getSessionState();
        if (!state.masterTabId || state.masterTabId === this.tabId) {return;}

        const heartbeatData = localStorage.getItem(STORAGE_KEYS.TAB_HEARTBEAT);
        if (!heartbeatData) {return;}

        try {
            const heartbeat = JSON.parse(heartbeatData);
            const timeSinceHeartbeat = Date.now() - heartbeat.timestamp;

            if (timeSinceHeartbeat > 15000 && heartbeat.tabId === state.masterTabId) {
                this.log('Master tab appears dead, triggering election');
                this.electMaster();
            }
        } catch (error) {
            this.log('Error checking heartbeat', {error});
        }
    }

    private electMaster(): void {
        const electionData = {
            candidateTabId: this.tabId,
            timestamp: Date.now(),
            priority: Date.now() + Math.random()
        };

        this.log('Starting master election', {priority: electionData.priority});
        localStorage.setItem(STORAGE_KEYS.MASTER_ELECTION, JSON.stringify(electionData));

        if (this.masterElectionTimeout) {
            clearTimeout(this.masterElectionTimeout);
        }

        this.masterElectionTimeout = setTimeout(() => {
            this.resolveMasterElection();
        }, 500);
    }

    private handleMasterElection(): void {
        if (this.masterElectionTimeout) {
            clearTimeout(this.masterElectionTimeout);
        }

        this.masterElectionTimeout = setTimeout(() => {
            this.resolveMasterElection();
        }, 500);
    }

    private resolveMasterElection(): void {
        const electionData = localStorage.getItem(STORAGE_KEYS.MASTER_ELECTION);
        if (!electionData) {return;}

        try {
            const election = JSON.parse(electionData);
            const wasMaster = this.isMaster;
            this.isMaster = election.candidateTabId === this.tabId;

            if (this.isMaster && !wasMaster) {
                this.becomeMaster();
            } else if (!this.isMaster && wasMaster) {
                this.resignMaster();
            }

            this.updateSessionState({masterTabId: election.candidateTabId});
            this.log('Master election resolved', {
                newMaster: election.candidateTabId,
                isMaster: this.isMaster
            });

        } catch (error) {
            this.log('Error resolving master election', {error});
        }
    }

    private becomeMaster(): void {
        this.log('Becoming master tab', {
            isConfigured: this.isConfigured,
            config: this.config,
            isLoggedIn: localStorage.getItem('isLoggedIn')
        });

        this.log('Becoming master tab', {isConfigured: this.isConfigured, config: this.config});
        this.broadcastEvent('master-changed', {newMaster: this.tabId});

        // Only start timers if properly configured AND session time is valid
        if (!this.isConfigured || this.config.sessionTime <= 0) {
            console.warn('[SessionManager] Skipping timer start - not configured or invalid session time', {
                isConfigured: this.isConfigured,
                sessionTime: this.config.sessionTime
            });
            this.log('Skipping timer start - SessionManager not configured yet');
            return;
        }

        const state = this.getSessionState();
        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

        this.log('New master checking session state', {
            isLoggedIn,
            isWarningActive: state.isWarningActive,
            hasWarningStartTime: !!state.warningStartTime,
            hasSessionEndTime: !!state.sessionEndTime,
            sessionEndTime: state.sessionEndTime
        });

        if (state.isWarningActive && state.warningStartTime) {
            this.resumeWarningCountdown(state.warningStartTime);
        } else if (isLoggedIn && state.sessionEndTime) {
            // Check if session is still valid
            const now = Date.now();
            const timeRemaining = state.sessionEndTime - now;

            if (timeRemaining > 0) {
                this.log('Inheriting active session timer as new master', {
                    timeRemaining: Math.round(timeRemaining / 1000),
                    sessionEndTime: new Date(state.sessionEndTime).toISOString()
                });
                this.log('Inheriting active session timer', {timeRemaining, sessionEndTime: state.sessionEndTime});

                // Calculate when warning should start
                const warningDelay = timeRemaining - (this.config.warningTime * 1000);
                if (warningDelay > 0) {
                    // Session timer with warning delay
                    this.warningTimer = setTimeout(() => {
                        this.startWarning();
                    }, warningDelay);
                    this.log('Session timer inherited with warning delay', {warningDelay});
                } else {
                    // Warning should start immediately
                    this.startWarning();
                }
            } else {
                this.log('Session expired - forcing logout');
                this.forceLogout();
            }
        } else if (isLoggedIn) {
            this.log('Starting fresh session timer as new master');
            this.startSessionTimer();
        } else {
            this.log('Not starting timer - user not logged in');
        }
    }

    private resignMaster(): void {
        this.log('Resigning as master tab');

        // Save current session state before clearing timers
        const currentState = this.getSessionState();
        if (this.warningTimer && currentState.sessionEndTime) {
            this.log('Preserving session state for new master', {
                sessionEndTime: currentState.sessionEndTime,
                isWarningActive: currentState.isWarningActive
            });
            // Update session state with current timing info for new master
            this.updateSessionState({
                sessionEndTime: currentState.sessionEndTime,
                isWarningActive: currentState.isWarningActive,
                warningStartTime: currentState.warningStartTime
            });
        }

        this.clearAllTimers();
    }

    private resumeWarningCountdown(warningStartTime: number): void {
        // Don't resume countdown if not properly configured
        if (!this.isConfigured || this.config.warningTime <= 0) {
            console.warn('[SessionManager] Cannot resume countdown - not configured', {
                isConfigured: this.isConfigured,
                warningTime: this.config.warningTime
            });
            return;
        }

        // Calculate absolute logout time from warning start time
        const logoutTime = warningStartTime + (this.config.warningTime * 1000);
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil((logoutTime - now) / 1000));

        this.log('Resuming countdown', {
            warningStartTime: new Date(warningStartTime).toISOString(),
            logoutTime: new Date(logoutTime).toISOString(),
            remaining,
            warningTime: this.config.warningTime
        });

        if (remaining <= 0) {
            this.log('Countdown expired - forcing logout');
            this.forceLogout();
            return;
        }

        this.startCountdown(logoutTime);
    }

    private handleSessionEvent(event: StorageEvent): void {
        if (!event.newValue) {return;}

        try {
            const sessionEvent: SessionEvent = JSON.parse(event.newValue);

            if (sessionEvent.tabId === this.tabId) {return;}

            this.log('Received session event', sessionEvent);
            this.notifyListeners(sessionEvent.type, sessionEvent);

            switch (sessionEvent.type) {
                case 'warning-started':
                    this.handleRemoteWarningStarted(sessionEvent);
                    break;
                case 'warning-ended':
                    this.handleRemoteWarningEnded();
                    break;
                case 'session-extended':
                    this.handleRemoteSessionExtended();
                    break;
                case 'activity-detected':
                    this.handleRemoteActivity();
                    break;
                case 'extension-request':
                    this.handleRemoteExtensionRequest();
                    break;
            }
        } catch (error) {
            this.log('Error handling session event', {error});
        }
    }

    private handleRemoteWarningStarted(event: SessionEvent): void {
        if (!this.isMaster) {
            const logoutTime = event.data?.logoutTime || (event.data?.warningStartTime + (this.config.warningTime * 1000));
            this.updateSessionState({
                isWarningActive: true,
                warningStartTime: event.data?.warningStartTime || event.timestamp,
                logoutTime
            });

            // Non-master tabs also need to start their own countdown based on the shared logoutTime
            this.log('Non-master tab starting countdown', {
                logoutTime: new Date(logoutTime).toISOString(),
                remainingSeconds: Math.ceil((logoutTime - Date.now()) / 1000)
            });
            this.startCountdown(logoutTime);
        }
    }

    private handleRemoteWarningEnded(): void {
        if (!this.isMaster) {
            // Use local clear to avoid event loop
            this.clearWarningStateLocal();
        }
    }

    private handleRemoteSessionExtended(): void {
        if (!this.isMaster) {
            this.updateSessionState({
                isWarningActive: false,
                warningStartTime: null,
                sessionExtendedAt: Date.now()
            });
        }
    }

    private handleRemoteActivity(): void {
        if (this.isMaster) {
            this.resetTimers();
        }
    }

    private handleRemoteExtensionRequest(): void {
        if (this.isMaster) {
            this.log('Master tab received extension request - extending session');
            // Master tab handles the actual extension
            this.extendSession();
        }
    }

    private handleForceLogout(): void {
        this.log('Force logout received');
        this.clearAllTimers();
        this.notifyListeners('force-logout', {
            type: 'force-logout',
            tabId: 'system',
            timestamp: Date.now()
        });
    }

    private broadcastEvent(type: SessionEventType, data?: any): void {
        const event: SessionEvent = {
            type,
            tabId: this.tabId,
            timestamp: Date.now(),
            data
        };

        localStorage.setItem(STORAGE_KEYS.SESSION_EVENT, JSON.stringify(event));
        this.log('Broadcasted event', event);

        // Also notify listeners in the current tab
        this.notifyListeners(type, event);
    }

    private getSessionState(): SessionState {
        const stored = localStorage.getItem(STORAGE_KEYS.SESSION_STATE);
        const defaultState: SessionState = {
            warningStartTime: null,
            sessionExtendedAt: null,
            lastActivity: Date.now(),
            isWarningActive: false,
            masterTabId: null,
            sessionEndTime: null,
            logoutTime: null
        };

        if (!stored) {return defaultState;}

        try {
            return {...defaultState, ...JSON.parse(stored)};
        } catch {
            return defaultState;
        }
    }

    private updateSessionState(updates: Partial<SessionState>): void {
        const currentState = this.getSessionState();
        const newState = {...currentState, ...updates};
        localStorage.setItem(STORAGE_KEYS.SESSION_STATE, JSON.stringify(newState));
    }

    private clearStaleSessionState(): void {
        const state = this.getSessionState();
        const now = Date.now();

        // If there's a warning that's older than our session time, clear it
        if (state.warningStartTime && (now - state.warningStartTime) > this.config.sessionTime) {

            this.log('Clearing stale warning state', {
                warningStartTime: state.warningStartTime,
                age: (now - state.warningStartTime) / 1000,
                sessionTime: this.config.sessionTime / 1000
            });

            this.updateSessionState({
                warningStartTime: null,
                isWarningActive: false,
                sessionExtendedAt: null,
                sessionEndTime: null,
                logoutTime: null
            });
        }

        // Check if sessionEndTime is in the past (stale from previous session)
        if (state.sessionEndTime && now > state.sessionEndTime) {
            this.log('Clearing expired sessionEndTime from previous session', {
                sessionEndTime: new Date(state.sessionEndTime).toISOString(),
                expired: (now - state.sessionEndTime) / 1000
            });

            this.updateSessionState({
                sessionEndTime: null,
                isWarningActive: false,
                warningStartTime: null,
                logoutTime: null
            });
        }

        // Also check if logoutTime is in the past
        if (state.logoutTime && now > state.logoutTime) {
            this.log('Clearing expired logoutTime', {
                logoutTime: new Date(state.logoutTime).toISOString(),
                expired: (now - state.logoutTime) / 1000
            });

            this.updateSessionState({
                logoutTime: null,
                isWarningActive: false,
                warningStartTime: null
            });
        }
    }

    private startSessionTimer(): void {
        if (!this.isMaster || !this.isConfigured || this.config.sessionTime === 0) {
            this.log('Skipping session timer start', {
                isMaster: this.isMaster,
                isConfigured: this.isConfigured,
                sessionTime: this.config.sessionTime
            });
            return;
        }

        this.clearAllTimers();

        const warningTimeMs = this.config.warningTime * 1000;
        const sessionTimeMs = this.config.sessionTime; // Already in milliseconds
        const warningDelay = sessionTimeMs - warningTimeMs;

        if (warningDelay <= 0) {
            this.log('Invalid timer configuration - warning delay is negative', {
                sessionTimeMs,
                warningTimeMs,
                warningDelay
            });
            return;
        }

        this.warningTimer = setTimeout(() => {
            this.startWarning();
        }, warningDelay);

        this.logoutTimer = setTimeout(() => {
            this.forceLogout();
        }, sessionTimeMs);

        this.updateSessionState({
            sessionEndTime: Date.now() + sessionTimeMs
        });

        this.log('Session timer started', {
            sessionTimeMs,
            warningTimeMs,
            warningDelay,
            sessionEndTime: new Date(Date.now() + sessionTimeMs).toISOString()
        });
    }

    private resetTimers(): void {
        if (!this.isMaster) {return;}

        this.log('Resetting session timers due to activity');
        this.clearWarningState();
        this.startSessionTimer();
    }

    private startWarning(): void {
        if (!this.isMaster) {return;}

        const warningStartTime = Date.now();
        const logoutTime = warningStartTime + (this.config.warningTime * 1000);

        this.updateSessionState({
            isWarningActive: true,
            warningStartTime,
            logoutTime
        });

        this.broadcastEvent('warning-started', {warningStartTime, logoutTime});
        this.startCountdown(logoutTime);

        this.log('Warning started', {warningTime: this.config.warningTime, logoutTime: new Date(logoutTime).toISOString()});
    }

    private startCountdown(logoutTime: number): void {
        // Update countdown every second based on absolute logout time
        this.countdownInterval = setInterval(() => {
            const now = Date.now();
            const remainingTime = Math.ceil((logoutTime - now) / 1000);

            if (remainingTime <= 0) {
                this.forceLogout();
                return;
            }

            // Broadcast countdown update so all tabs can show the same time
            this.notifyListeners('warning-started', {
                type: 'warning-started',
                tabId: this.tabId,
                timestamp: now,
                data: {remainingTime, logoutTime}
            });
        }, 1000);

        // Trigger initial notification
        const initialRemainingTime = Math.ceil((logoutTime - Date.now()) / 1000);
        this.notifyListeners('warning-started', {
            type: 'warning-started',
            tabId: this.tabId,
            timestamp: Date.now(),
            data: {remainingTime: initialRemainingTime, logoutTime}
        });
    }

    private clearWarningState(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }

        this.updateSessionState({
            isWarningActive: false,
            warningStartTime: null,
            logoutTime: null
        });

        this.broadcastEvent('warning-ended');
        this.log('Warning state cleared');
    }

    private clearWarningStateLocal(): void {
        // Clear warning state WITHOUT broadcasting (for responding to remote events)
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }

        this.updateSessionState({
            isWarningActive: false,
            warningStartTime: null,
            logoutTime: null
        });

        this.log('Warning state cleared locally (no broadcast)');
    }

    private clearAllTimers(): void {
        [this.warningTimer, this.logoutTimer].forEach(timer => {
            if (timer) {clearTimeout(timer);}
        });

        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }

        this.warningTimer = null;
        this.logoutTimer = null;
        this.countdownInterval = null;
    }

    private forceLogout(): void {
        // Always log force logout (critical event)
        console.error('[SessionManager] FORCE LOGOUT TRIGGERED', {
            tabId: this.tabId,
            isMaster: this.isMaster,
            isConfigured: this.isConfigured,
            config: this.config
        });

        this.log('Forcing logout');
        this.clearAllTimers();
        localStorage.setItem(STORAGE_KEYS.FORCE_LOGOUT, Date.now().toString());
        localStorage.removeItem('isLoggedIn'); // Clear login flag

        setTimeout(() => {
            window.location.href = (window as any).URLS?.logOutView || '/logout';
        }, 100);
    }

    public initialize(config: SessionConfig): void {
        // Always log configuration attempts
        this.log('Configuration attempt', {...config, isMaster: this.isMaster});

        this.config = config;
        this.log('SessionManager configure attempt', config);

        // Validate config
        if (config.sessionTime <= 0 || config.warningTime <= 0) {
            console.error('[SessionManager] Invalid configuration - skipping timer initialization', config);
            this.log('Invalid session configuration, skipping timer initialization', config);
            this.isConfigured = false;
            return;
        }

        if (config.warningTime * 1000 >= config.sessionTime) {
            console.warn('[SessionManager] Warning time too large - adjusting', config);
            this.log('Warning time is greater than or equal to session time, adjusting', config);
            this.config.warningTime = Math.floor(config.sessionTime / 1000 / 2); // Set to half of session time
        }

        // Clear any stale session state from previous sessions
        this.clearStaleSessionState();

        this.isConfigured = true;
        this.log('Successfully configured', {...this.config, isMaster: this.isMaster, isLoggedIn: localStorage.getItem('isLoggedIn')});
        this.log('SessionManager successfully configured', this.config);

        // If we're master and logged in, start the timer NOW (regardless of when we became master)
        if (this.isMaster && localStorage.getItem('isLoggedIn') === 'true') {
            this.log('Starting session timer - master tab with configuration');
            setTimeout(() => {
                this.startSessionTimer();
            }, 1000);
        } else {
            this.log('Not starting timer:', {
                isMaster: this.isMaster,
                isLoggedIn: localStorage.getItem('isLoggedIn')
            });
        }
    }

    public async extendSession(): Promise<boolean> {
        if (!this.isMaster) {
            this.log('Non-master tab requesting session extension - delegating to master');
            // Broadcast extension request to master tab
            this.broadcastEvent('extension-request');
            return true; // Return true so UI updates, master will handle the actual request
        }

        try {
            this.log('Attempting session extension');

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            const response = await fetch((window as any).URLS?.pingSession || '/api/ping-session', {
                method: 'POST',
                credentials: 'include',
                signal: controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                this.clearWarningState();
                this.startSessionTimer();
                this.broadcastEvent('session-extended');
                this.log('Session extended successfully');
                return true;
            } else {
                this.log('Session extension failed', {status: response.status, statusText: response.statusText});
                return false;
            }
        } catch (error: any) {
            if (error.name === 'AbortError') {
                this.log('Session extension timed out');
            } else {
                this.log('Session extension error', {error: error.message || error});
            }
            return false;
        }
    }

    public addEventListener(type: SessionEventType, callback: (event: SessionEvent) => void): void {
        if (!this.listeners.has(type)) {
            this.listeners.set(type, new Set());
        }
        this.listeners.get(type)!.add(callback);
    }

    public removeEventListener(type: SessionEventType, callback: (event: SessionEvent) => void): void {
        this.listeners.get(type)?.delete(callback);
    }

    private notifyListeners(type: SessionEventType, event: SessionEvent): void {
        this.listeners.get(type)?.forEach(callback => {
            try {
                callback(event);
            } catch (error) {
                this.log('Error in event listener', {type, error});
            }
        });
    }

    public getState(): SessionState {
        return this.getSessionState();
    }

    public isMasterTab(): boolean {
        return this.isMaster;
    }

    public getTabId(): string {
        return this.tabId;
    }

    public isDebugMode(): boolean {
        return isSessionDebugMode();
    }

    public trackApiRequest(): void {
        this.log('API request detected - resetting session timer');

        if (this.activityDebounceTimeout) {
            clearTimeout(this.activityDebounceTimeout);
        }

        this.activityDebounceTimeout = setTimeout(() => {
            const now = Date.now();
            if (now - this.lastActivityBroadcast > this.ACTIVITY_DEBOUNCE_MS) {
                this.log('Broadcasting API activity to all tabs');
                this.broadcastActivity();
                this.lastActivityBroadcast = now;
            }
        }, 100);
    }

    public destroy(): void {
        this.clearAllTimers();

        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }

        if (this.activityDebounceTimeout) {
            clearTimeout(this.activityDebounceTimeout);
        }

        ACTIVITY_EVENTS.forEach(event => {
            window.removeEventListener(event, this.handleActivity);
        });

        window.removeEventListener('storage', this.setupStorageListener);

        this.log('SessionManager destroyed');
    }
}

export default SessionManager;
export {isSessionDebugMode};
export type {SessionState, SessionConfig, SessionEvent, SessionEventType};